import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { API_ENDPOINTS } from '../shared/constants/api.constants';
import { StorageService } from './storage.service';
import { haversineDistanceKm } from '../shared/utility/geo.util';
import { CategoryService } from './category.service';
import { RequestCacheService } from '../core/http/request-cache.service';
import {
  CategorySearchResult,
  ProductSearchPage,
  ProductSearchResult,
  SearchResults,
  ServiceSearchResult,
  ShopSearchResult,
  emptySearchResults,
} from '../models/search.model';

export const PRODUCT_PAGE_SIZE = 20;
const SHOP_PAGE_SIZE = '20';
const MAX_SERVICE_SHOPS_TO_SCAN = 6;
const SERVICE_SHOP_CACHE_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class SearchService {

  // Categories rarely change within a session - shared with CategoryService's cache.
  private categories$: Observable<any[]>;

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private categoryService: CategoryService,
    private cache: RequestCacheService,
  ) {
    this.categories$ = this.categoryService.getCategories().pipe(
      map((res: any) => res?.results || []),
      catchError(() => of([])),
      shareReplay(1)
    );
  }

  getSuggestions(query: string): Observable<any[]> {
    return this.http.get<any[]>(API_ENDPOINTS.SEARCH_SUGGESTIONS, {
      params: { q: query, limit: '8' }
    });
  }

  /** Popular search terms from the backend (cached 10 min). Empty list on failure - never invented. */
  getQuickSuggestions(): Observable<string[]> {
    return this.cache.get('quick-suggestions', 10 * 60 * 1000, () =>
      this.http.get<any>(API_ENDPOINTS.QUICK_SUGGESTIONS)
    ).pipe(
      map(res => {
        const list = Array.isArray(res) ? res : (res?.results || res?.suggestions || []);
        return list.map((s: any) => typeof s === 'string' ? s : (s?.query || s?.name || '')).filter(Boolean);
      }),
      catchError(() => of([] as string[]))
    );
  }

  /** Typeahead product search, scoped to the delivery pincode when one is set. */
  liveProductSearch(query: string, pageSize = 6): Observable<any[]> {
    return this.http.get<any>(API_ENDPOINTS.PRODUCTS_V2, { params: this.productParams(query, 1, pageSize) }).pipe(
      map(res => res?.results || []),
      catchError(() => of([]))
    );
  }

  search(params: Record<string, string>): Observable<any> {
    return this.http.get<any>(API_ENDPOINTS.SEARCH, { params });
  }

  /**
   * Unified marketplace search across products, shops, services and categories.
   * Products and shops use the backend's search filters scoped to the active
   * location; categories are matched against the cached category list; services
   * are found by searching the services of nearby service shops, since the
   * backend has no cross-shop service search endpoint yet.
   */
  searchEverything(query: string): Observable<SearchResults> {
    const q = query.trim();
    if (!q) {
      return of(emptySearchResults(q));
    }

    return forkJoin({
      productPage: this.searchProducts(q, 1),
      shops: this.searchShops(q),
      categories: this.searchCategories(q),
    }).pipe(
      switchMap(({ productPage, shops, categories }) =>
        this.searchServices(q, shops).pipe(
          map(services => ({
            query: q,
            products: productPage.items,
            productsHasMore: productPage.hasMore,
            productsCount: productPage.count,
            shops,
            services,
            categories,
          }))
        )
      )
    );
  }

  /** One page of product results (for "load more"). */
  searchProducts(query: string, page: number): Observable<ProductSearchPage> {
    return this.http.get<any>(API_ENDPOINTS.PRODUCTS_V2, { params: this.productParams(query, page, PRODUCT_PAGE_SIZE) }).pipe(
      map(res => ({
        items: (res?.results || []) as ProductSearchResult[],
        hasMore: !!res?.next,
        count: Number(res?.count) || 0,
      })),
      catchError(() => of({ items: [], hasMore: false, count: 0 }))
    );
  }

  /** Server-side pincode filter (same one the category pages use), not client-side filtering of one page. */
  private productParams(query: string, page: number, pageSize: number): HttpParams {
    let params = new HttpParams().set('search', query).set('page', String(page)).set('page_size', String(pageSize));
    const pincode = this.storage.getItem('postal_code');
    if (pincode) {
      params = params.set('shop__address__pincode', pincode);
    }
    return params;
  }

  /** Same location scoping as the home page's shop list: coordinates + radius, else pincode. */
  private shopParams(extra: Record<string, string> = {}): HttpParams {
    let params = new HttpParams({ fromObject: { page_size: SHOP_PAGE_SIZE, ...extra } });
    const lat = this.storage.getItem('latitude');
    const lng = this.storage.getItem('longitude');
    const pincode = this.storage.getItem('postal_code');
    if (lat && lng) {
      params = params.set('lat', lat).set('lng', lng).set('radius', this.storage.getItem('radius') || '1');
    } else if (pincode) {
      params = params.set('address__pincode', pincode);
    }
    return params;
  }

  private searchShops(query: string): Observable<ShopSearchResult[]> {
    return this.http.get<any>(API_ENDPOINTS.STORES_V2, { params: this.shopParams({ search: query }) }).pipe(
      map(res => (res.results || []).map(this.mapShop)),
      catchError(() => of([]))
    );
  }

  private searchCategories(query: string): Observable<CategorySearchResult[]> {
    const needle = query.toLowerCase();
    return this.categories$.pipe(
      map((categories: any[]) => categories
        .filter(c => c.name?.toLowerCase().includes(needle))
        .map(this.mapCategory)
      )
    );
  }

  /** Nearby shops, cached per location so repeated searches don't refetch them. */
  private nearbyShops(): Observable<any[]> {
    const params = this.shopParams({ page_size: '50' });
    return this.cache.get(`search-shops:${params.toString()}`, SERVICE_SHOP_CACHE_MS, () =>
      this.http.get<any>(API_ENDPOINTS.STORES_V2, { params })
    ).pipe(
      map(res => (res?.results || []) as any[]),
      catchError(() => of([] as any[]))
    );
  }

  private isServiceShop(shop: any, serviceCategoryIds: Set<number>): boolean {
    const ids: number[] = Array.isArray(shop.category) ? shop.category : (shop.categories || []).map((c: any) => c.id);
    return !!shop.is_service_provider || shop.shop_type === 'service' || shop.shop_type === 'hybrid'
      || ids.some(id => serviceCategoryIds.has(id));
  }

  private searchServices(query: string, matchedShops: ShopSearchResult[]): Observable<ServiceSearchResult[]> {
    const needle = query.toLowerCase();
    const matchedShopSlugs = new Set(matchedShops.map(s => s.slug));

    return forkJoin({ categories: this.categories$, shops: this.nearbyShops() }).pipe(
      switchMap(({ categories, shops }) => {
        const serviceCategoryIds = new Set<number>(
          categories.filter(c => c.category_type === 'service').map(c => c.id)
        );
        const serviceCategoryMatch = categories.some(c => c.category_type === 'service' && c.name?.toLowerCase().includes(needle));

        // Shops whose name matched come first, then other nearby service shops.
        const candidates = shops.filter(s => this.isServiceShop(s, serviceCategoryIds));
        const prioritized = [
          ...candidates.filter(s => matchedShopSlugs.has(s.slug)),
          ...candidates.filter(s => !matchedShopSlugs.has(s.slug)),
        ].slice(0, MAX_SERVICE_SHOPS_TO_SCAN);

        if (!prioritized.length) {
          return of([] as ServiceSearchResult[]);
        }

        return forkJoin(prioritized.map(shop => {
          // A name/category match on the shop lists all its services; otherwise ask
          // the backend to search the shop's services for the term.
          const shopMatched = matchedShopSlugs.has(shop.slug) || serviceCategoryMatch;
          const params: Record<string, string> = shopMatched ? { page_size: '10' } : { page_size: '10', search: query };
          return this.http.get<any>(`${API_ENDPOINTS.SHOP_SERVICES}/${shop.slug}/services/`, { params }).pipe(
            map(res => (res.results || []).map((svc: any) => this.mapService(svc, shop))),
            map((list: ServiceSearchResult[]) => shopMatched ? list : list.filter(sv =>
              sv.name.toLowerCase().includes(needle) || sv.categoryName.toLowerCase().includes(needle) || sv.description.toLowerCase().includes(needle))),
            catchError(() => of([] as ServiceSearchResult[]))
          );
        })).pipe(map(lists => lists.flat()));
      })
    );
  }

  private mapShop = (s: any): ShopSearchResult => {
    const userLat = parseFloat(this.storage.getItem('latitude') || '');
    const userLng = parseFloat(this.storage.getItem('longitude') || '');
    const shopLat = parseFloat(s.location?.latitude);
    const shopLng = parseFloat(s.location?.longitude);
    const hasCoords = !isNaN(userLat) && !isNaN(userLng) && !isNaN(shopLat) && !isNaN(shopLng);
    const apiDistance = Number(s.distance);

    return {
      id: s.id,
      slug: s.slug,
      name: s.name,
      image: s.shop_image,
      rating: s.rating ?? 0,
      reviewCount: s.review_count ?? 0,
      isAcceptingOrders: s.is_accepting_orders ?? true,
      categoryName: s.category_name || s.categories?.[0]?.name || '',
      city: s.location?.city,
      isServiceProvider: !!s.is_service_provider || s.shop_type === 'service' || s.shop_type === 'hybrid',
      distanceKm: Number.isFinite(apiDistance) && s.distance != null
        ? apiDistance
        : hasCoords ? haversineDistanceKm(userLat, userLng, shopLat, shopLng) : undefined,
    };
  };

  private mapCategory = (c: any): CategorySearchResult => ({
    id: c.id,
    slug: c.slug,
    name: c.name,
    image: c.image,
    categoryType: c.category_type,
  });

  private mapService = (svc: any, shop: any): ServiceSearchResult => ({
    id: svc.id ?? svc.service?.id,
    slug: svc.slug || svc.service?.slug || String(svc.id),
    name: svc.name || svc.service?.name || '',
    description: svc.short_description || svc.service?.short_description || '',
    image: svc.image || svc.service?.image || svc.cover_image || null,
    basePrice: Number(svc.base_price ?? svc.service?.base_price) || 0,
    durationMinutes: Number(svc.estimated_duration_minutes ?? svc.service?.estimated_duration_minutes) || 0,
    shopName: shop.name,
    shopSlug: shop.slug,
    categoryName: svc.category_name || svc.service?.category_name || '',
  });
}
